import type { JSX } from 'react';
import { Moon, Sun, LogOut, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UserAvatar } from '@/components/atoms/UserAvatar';
import { useAuth } from '@/features/auth';
import { useCandidateSession } from '../context/CandidateSessionContext';

function ThemeToggle(): JSX.Element {
  const { theme, setTheme } = useTheme();

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
    >
      <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      <span className="sr-only">Toggle theme</span>
    </Button>
  );
}

function UserMenu(): JSX.Element {
  const { user, logout } = useAuth();
  const fullName = user?.profile.fullName ?? 'User';
  const photoUrl = user?.profile.photoUrl;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative h-9 w-9 rounded-full"
          data-testid="user-profile-menu"
        >
          <UserAvatar fullName={fullName} photoUrl={photoUrl} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium" data-testid="user-name">
              {fullName}
            </p>
            <p className="text-xs text-muted-foreground" data-testid="user-email">
              {user?.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem>
          <User className="mr-2 h-4 w-4" />
          Profile
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout()} data-testid="logout-button">
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CandidateHeader(): JSX.Element {
  const { isAnonymous, shareId } = useCandidateSession();

  return (
    <header className="flex h-14 items-center justify-end gap-2 border-b px-4">
      <ThemeToggle />
      {isAnonymous ? (
        <Button asChild variant="default" size="sm" data-testid="create-account-button">
          <Link to={`/candidate/${shareId}/register`}>Create Account</Link>
        </Button>
      ) : (
        <UserMenu />
      )}
    </header>
  );
}
